import axios, { type AxiosError } from 'axios';

export interface ApiErrorResponse {
  message?: string | string[];
}

export interface ApiErrorMappingOptions {
  /** User-facing copy for specific HTTP statuses. */
  statusMap?: Record<number, string>;
  /** User-facing copy for specific backend error messages or codes. */
  messageMap?: Record<string, string>;
  /** Runs first; return a message to override every other mapping. */
  customHandler?: (error: AxiosError<ApiErrorResponse>) => string | undefined;
  /** Used when nothing else matches. Defaults to DEFAULT_API_ERROR_MESSAGE. */
  fallback?: string;
}

export const DEFAULT_API_ERROR_MESSAGE = 'Something went wrong. Please try again.';
const TIMEOUT_MESSAGE = 'The request took too long. Please try again.';

/** Backend error messages from a response body, as a list. */
export function getApiErrorMessages(error: AxiosError<ApiErrorResponse>): string[] {
  const message = error.response?.data?.message;
  return (Array.isArray(message) ? message : [message]).filter(
    (value): value is string => typeof value === 'string' && value !== ''
  );
}

/**
 * Extracts a user-facing message from an unknown error without requiring
 * callers (page components) to import axios or reason about AxiosError shapes
 * directly. Owns generic handling (timeouts, non-Axios errors); feature-specific
 * copy is supplied by the caller through `options`.
 */
export function getApiErrorMessage(error: unknown, options: ApiErrorMappingOptions = {}): string {
  const fallback = options.fallback ?? DEFAULT_API_ERROR_MESSAGE;
  if (!axios.isAxiosError<ApiErrorResponse>(error)) return fallback;

  const customMessage = options.customHandler?.(error);
  if (customMessage) return customMessage;

  for (const message of getApiErrorMessages(error)) {
    const mapped = options.messageMap?.[message];
    if (mapped) return mapped;
  }

  const status = error.response?.status;
  const mappedStatus = status === undefined ? undefined : options.statusMap?.[status];
  if (mappedStatus) return mappedStatus;

  if (error.code === 'ECONNABORTED' || status === 408 || status === 504) return TIMEOUT_MESSAGE;

  return fallback;
}
